import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { profiles } from "../services/supabase";
import { Profile } from "../types/database";
import "./profile.css";

const ProfilePage: React.FC = () => {
  const { user, changePassword } = useUser();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");

  const [fullName, setFullName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [tshirtSize, setTshirtSize] = useState<string>("");

  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
  const [pwError, setPwError] = useState<string>("");
  const [pwMessage, setPwMessage] = useState<string>("");
  const [changingPassword, setChangingPassword] = useState<boolean>(false);
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      navigate("/account");
      return;
    }

    const loadProfile = async () => {
      try {
        const { data, error } = await profiles.getById(user.id);
        if (error) throw error;

        if (data) {
          setProfile(data);
          setFullName(data.full_name || "");
          setUsername(data.username || "");
          setGender(data.gender || "");
          setTshirtSize(data.tshirt_size || "");
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, navigate]);

  const handleSave = async () => {
    if (!user) return;

    if (!fullName.trim()) { setError("Full name is required"); return; }
    if (!username.trim()) { setError("Username is required"); return; }
    if (!gender)          { setError("Please select your gender"); return; }
    if (!tshirtSize)      { setError("Please select your t-shirt size"); return; }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const updates: Partial<Profile> = {
        full_name: fullName.trim(),
        username: username.trim(),
        gender,
        tshirt_size: tshirtSize,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await profiles.update(user.id, updates);
      if (error) throw error;

      setProfile(data);
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Failed to update profile:", err);
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError("");
    setPwMessage("");

    if (!currentPassword)             { setPwError("Enter your current password."); return; }
    if (newPassword.length < 8)       { setPwError("New password must be at least 8 characters."); return; }
    if (newPassword !== confirmNewPassword) { setPwError("New passwords do not match."); return; }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPwMessage("Password updated successfully. Other active sessions have been signed out.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setPwError(err instanceof Error ? err.message : "Failed to update password.");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPwError("");
    setPwMessage("");
    setShowPasswordForm(false);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <p className="pp-loading">Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <p className="pp-loading">Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="profile-page">

      {/* Header */}
      <div className="pp-header">
        <div className="pp-avatar">
          {fullName.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?'}
        </div>
        <div>
          <h1 className="pp-title">Your Profile</h1>
          <p className="pp-email">{user.email}</p>
        </div>
      </div>

      {/* Messages */}
      {error   && <div className="pp-error">{error}</div>}
      {message && <div className="pp-success">{message}</div>}

      {/* Form */}
      <div className="pp-form">

        <div className="pp-field">
          <label className="pp-label">Full Name *</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="pp-input"
            placeholder="John Doe"
            disabled={saving}
          />
        </div>

        <div className="pp-field">
          <label className="pp-label">Username *</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="pp-input"
            placeholder="johndoe"
            disabled={saving}
          />
        </div>

        <div className="pp-field">
          <label className="pp-label">Gender *</label>
          <div className="pp-select-wrap">
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="pp-select"
              disabled={saving}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </div>
        </div>

        <div className="pp-field">
          <label className="pp-label">
            T-Shirt Size * <span className="pp-label-hint">(for kit orders)</span>
          </label>
          <div className="pp-select-wrap">
            <select
              value={tshirtSize}
              onChange={(e) => setTshirtSize(e.target.value)}
              className="pp-select"
              disabled={saving}
            >
              <option value="">Select Size</option>
              <option value="XS">XS</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="XXL">XXL</option>
            </select>
          </div>
          <p className="pp-hint">This helps your team captain when ordering kits</p>
        </div>

        <div className="pp-divider-field">
          <div className="pp-divider-label">Account Email</div>
          <div className="pp-divider-value">{user.email}</div>
          <div className="pp-divider-hint">Contact support to change your email address</div>
        </div>

        <div className="pp-actions">
          <button onClick={() => navigate(-1)} disabled={saving} className="pp-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="pp-btn-primary">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="pp-form">
        <div className="pp-divider-label">Change Password</div>

        {!showPasswordForm && (
          <div className="pp-actions">
            <button onClick={() => setShowPasswordForm(true)} className="pp-btn-secondary">
              Change Password
            </button>
          </div>
        )}

        {showPasswordForm && (
          <>
            {pwError   && <div className="pp-error">{pwError}</div>}
            {pwMessage && <div className="pp-success">{pwMessage}</div>}

            <div className="pp-field">
              <label className="pp-label">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pp-input"
                placeholder="••••••••"
                disabled={changingPassword}
              />
            </div>

            <div className="pp-field">
              <label className="pp-label">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pp-input"
                placeholder="••••••••"
                disabled={changingPassword}
              />
            </div>

            <div className="pp-field">
              <label className="pp-label">Confirm New Password</label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="pp-input"
                placeholder="••••••••"
                disabled={changingPassword}
              />
            </div>

            <div className="pp-actions">
              <button onClick={handleCancelPasswordChange} disabled={changingPassword} className="pp-btn-secondary">
                Cancel
              </button>
              <button onClick={handleChangePassword} disabled={changingPassword} className="pp-btn-primary">
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Meta card */}
      {profile && (
        <div className="pp-meta">
          <div className="pp-meta-label">Account Info</div>
          <div className="pp-meta-row">
            <span className="pp-meta-key">Created</span>
            <span className="pp-meta-val">
              {new Date(profile.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>
          <div className="pp-meta-row">
            <span className="pp-meta-key">Last Updated</span>
            <span className="pp-meta-val">
              {new Date(profile.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
